// Optimized iOS-like Page Transition in TypeScript
// Improvements:
// - Lazy load DetailPro to reduce lag
// - Unmount ChartPro when not on home to avoid heavy background rendering
// - Simplify UnderPage animation to opacity instead of scale
// - Optimize drag logic

import { useDevice } from '@/hooks/useDevice';
import clsx from 'clsx';
import { Suspense, lazy, useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useSearchParams } from 'react-router';

import { ChartLite } from '../chart/chart-component';
import ErrorBoundary from '../error-boundary';
import { IOSPage, UnderPage } from '../IOSPage/IOSPage';
import { BuySell } from './buy-sell';
import { ClaimDailyAion } from './claim-aion/claim-daily-aion';
import { useGetLeverage } from './hooks/useGetLeverage';
import { LiteMode } from './lite-mode';
import { MODE_KEY, Mode } from './mode';
import { FormValue, ModeType, OrderType, Type } from './type';

const ChartPro = lazy(() =>
    import('../chart-pro').then((module) => ({
        default: module.ChartPro,
    }))
);

const DetailPro = lazy(() =>
    import('./detail-pro/detail-pro').then((module) => ({
        default: module.default,
    }))
);

function useFutureForm() {
    const [searchParams] = useSearchParams();

    const initialMode = (localStorage.getItem(MODE_KEY) as ModeType) || 'LITE';
    const pairIndex =
        searchParams.get('pairIndex') ??
        sessionStorage.getItem('pairIndex') ??
        '0';

    const { maxLeverage, initLeverage } = useGetLeverage(Number(pairIndex));

    const methods = useForm<FormValue>({
        defaultValues: {
            type: Type.Long,
            orderType: OrderType.Market,
            leverage: maxLeverage,
            limitPrice: '',
            pairIndex,
            amount: initLeverage.toString(),
            mode: initialMode,
            tp: '',
            sl: '',
            unit: 'USD',
        },
    });

    useEffect(() => {
        sessionStorage.setItem('pairIndex', pairIndex);
        methods.setValue('pairIndex', pairIndex);
    }, [methods, pairIndex]);

    return methods;
}

function LiteModeView() {
    return (
        <>
            <ChartLite />
            <LiteMode />
        </>
    );
}

export default function ProModeView() {
    const [route, setRoute] = useState('home');

    return (
        <div className="relative w-full h-full rounded-2xl border ">
            <UnderPage>
                {route === 'home' && (
                    <ProMode goDetail={() => setRoute('detail')} />
                )}
            </UnderPage>

            <IOSPage
                isActive={route === 'detail'}
                onBackSwipe={() => setRoute('home')}
            >
                <Suspense fallback={<div>Loading...</div>}>
                    <DetailPro onClose={() => setRoute('home')} />
                </Suspense>
            </IOSPage>
        </div>
    );
}

function ProMode({ goDetail }: { goDetail: () => void }) {
    const device = useDevice();

    return (
        <ErrorBoundary>
            <div className="relative">
                <Suspense
                    fallback={
                        <div className="h-[80vh] flex items-center justify-center">
                            <span className="loader"></span>
                        </div>
                    }
                >
                    {device !== 'Android' && <ChartPro />}
                </Suspense>

                <BuySell goDetail={goDetail} />
            </div>
        </ErrorBoundary>
    );
}

export function Future() {
    const methods = useFutureForm();
    const activeMode = useWatch({ control: methods.control, name: 'mode' });

    return (
        <FormProvider {...methods}>
            <div
                className={clsx({
                    'pb-[4rem]': activeMode === 'PRO',
                })}
            >
                <ClaimDailyAion />
                <Mode />

                {activeMode === 'LITE' ? <LiteModeView /> : <ProModeView />}
            </div>
        </FormProvider>
    );
}
